import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Pagination,
  Breadcrumb,
} from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchData,
  postData,
  patchData,
  deleteData,
} from "../../api/apiService";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import { usePermissions } from "carehub_auth_permissions";
import Loader from "../../ui/Layout/Loader";
import moment from "moment-timezone";

const DownTime = () => {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const { permissions, permissionsLoaded } = usePermissions();

  const loadData = async () => {
    try {
      const res = await fetchData("/maintenance");
      setMaintenances(res.maintenances || []);
      setTotalPages(
        Math.max(1, Math.ceil(res.maintenanceSCount / itemsPerPage))
      );
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data.");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const getCSTDateTimeLocal = (offsetHours = 0) => {
    return moment
      .tz("America/Chicago")
      .add(offsetHours, "hours")
      .format("YYYY-MM-DDTHH:mm");
  };

  const resetForm = () => {
    setMessage("");
    setStartDateTime("");
    setEndDateTime("");
    setSelectedId(null);
    setEditMode(false);
  };

  const openAddModal = () => {
    const todayStart = getCSTDateTimeLocal();
    const defaultStart = getCSTDateTimeLocal(1);
    const defaultEnd = getCSTDateTimeLocal(2);
    resetForm();
    setStartDateTime(defaultStart);
    setEndDateTime(defaultEnd);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditMode(true);
    setSelectedId(item.id);
    setMessage(item.message);
    setStartDateTime(item.startDateTime);
    setEndDateTime(item.endDateTime);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!message || !startDateTime || !endDateTime) {
      toast.error("All fields are required.");
      return;
    }

    const nowCST = moment.tz("America/Chicago");

    const start = moment.tz(startDateTime, "America/Chicago");
    const end = moment.tz(endDateTime, "America/Chicago");

    if (start.isBefore(nowCST)) {
      toast.error("Maintenance start date and time cannot be in the past.");
      return;
    }

    if (end.isBefore(nowCST)) {
      toast.error("Maintenance end date and time cannot be in the past.");
      return;
    }

    if (!end.isAfter(start)) {
      toast.error("End date and time must be after start date and time.");
      return;
    }

    const payload = {
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      message,
    };

    try {
      if (editMode) {
        await patchData(`/maintenance/${selectedId}`, payload);
        toast.success("Maintenance updated.");
      } else {
        await postData("/maintenance", payload);
        toast.success("Maintenance created.");
      }

      setShowModal(false);
      await loadData();
    } catch (err) {
      if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Error saving data.");
      }
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteData(`/maintenance/${deleteId}`);
      toast.success("Maintenance deleted.");
      setShowDeleteModal(false);
      await loadData();
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      {!permissionsLoaded ? (
        <Loader />
      ) : permissions.includes("administration_downtime") &&
        permissions.includes("downtime_list") ? (
        <Container className="mt-2">
          <ToastContainer />
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/administration" className="text-dark link">
                Administration
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>System Downtime</Breadcrumb.Item>
          </Breadcrumb>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>System Downtime Schedule</h3>
            {permissionsLoaded && permissions?.includes("downtime_add") && (
              <Button className="primary-btn" onClick={openAddModal}>
                <FaPlus className="me-2" />
                Add Downtime
              </Button>
            )}
          </div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Start (CST)</th>
                <th>End (CST)</th>
                <th>Message</th>
                <th>Status</th>
                <th>Created By</th>
                {permissionsLoaded &&
                  (permissions.includes("downtime_update") ||
                    permissions.includes("downtime_delete")) && (
                    <th>Actions</th>
                  )}
              </tr>
            </thead>
            <tbody>
              {maintenances.length > 0 ? (
                maintenances
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage
                  )
                  .map((m, index) => (
                    <tr key={m.id}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>{m.startDateTime}</td>
                      <td>{m.endDateTime}</td>
                      <td>{m.message}</td>
                      <td>
                        {m.status
                          ? m.status.charAt(0).toUpperCase() +
                            m.status.slice(1).toLowerCase()
                          : ""}
                      </td>
                      <td>{m.createdBy}</td>
                      <td>
                        {permissionsLoaded &&
                        (permissions.includes("downtime_update") ||
                          permissions.includes("downtime_delete")) ? (
                          m.status?.toLowerCase() !== "completed" ? (
                            <>
                              {permissions.includes("downtime_update") && (
                                <Button
                                  className="me-2 btn btn-secondary"
                                  onClick={() => openEditModal(m)}
                                >
                                  <MdEdit />
                                </Button>
                              )}
                              {permissions.includes("downtime_delete") && (
                                <Button
                                  variant="danger"
                                  onClick={() => handleDelete(m.id)}
                                >
                                  <FaTrash />
                                </Button>
                              )}
                            </>
                          ) : (
                            <span className="text-muted"></span>
                          )
                        ) : null}
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  {/* Adjust colSpan to match total columns; here we have 7 */}
                  <td colSpan="7" className="text-center">
                    No maintenance records found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end">
            <Pagination>
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>

          {/* Add / Edit Modal */}
          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {editMode ? "Edit Downtime" : "Add Downtime"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date & Time (CST)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={startDateTime}
                    min={getCSTDateTimeLocal()}
                    onChange={(e) => setStartDateTime(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>End Date & Time (CST)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={endDateTime}
                    min={startDateTime || getCSTDateTimeLocal()}
                    onChange={(e) => setEndDateTime(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button className="primary-btn" onClick={handleSubmit}>
                {editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Delete Modal */}
          <Modal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete this record?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      ) : (
        <Navigate to="/403" replace />
      )}
    </div>
  );
};

export default DownTime;
